/**
 * queryKey kuralı — sözleşme §6.
 *
 * Kaynak veritabanına dayanan her query `['source', <kaynak>, <tablo>, ...]` ile başlar,
 * çünkü SSE olayı geldiğinde invalidation tam olarak bu önekle yapılır:
 *   queryClient.invalidateQueries({ queryKey: ['source', e.source, e.table] })
 *
 * `<tablo>` değerleri olaydan gelir. Sözleşmede örneklenen tek değer `dbo.TARTIM`;
 * Canlı Tartım ve Kantar Raporu ekranları bu tabloya dayanır.
 */
export const SOURCE_KANTAR = 'kantar'
export const TABLE_TARTIM = 'dbo.TARTIM'
export const TABLE_BANKA = 'public.v_birlesik_sirket_verileri'

/**
 * `dbo.TARTIM` değişince hangi Cube view'larının tazelenmesi gerektiği (bkz. G3.2,
 * `hooks/use-realtime.ts`). İkinci bir kaynak/tablo eklendiğinde burada büyür.
 */
export const CUBES_BY_SOURCE_TABLE: Record<string, string[]> = {
  [TABLE_TARTIM]: ['CanliTartim', 'Tonaj'],
  [TABLE_BANKA]: ['BankaHareketleri', 'BankaBakiyeleri', 'BankaDonemSonuBakiyeleri'],
}

type KeyPart = string | number | boolean | null | undefined | Record<string, unknown>

export const queryKeys = {
  auth: {
    me: () => ['auth', 'me'] as const,
    sessions: () => ['auth', 'sessions'] as const,
  },

  /** Mutabakat elle düzeltmeleri (Cube değil, backend). */
  reconcileOverrides: () => ['reconcile', 'overrides'] as const,

  /** Claude bağlantısı onay isteğinin özeti. */
  mcpConsent: (request: string) => ['mcp', 'consent', request] as const,

  admin: {
    users: (params?: KeyPart) => ['admin', 'users', params ?? null] as const,
    user: (id: string) => ['admin', 'user', id] as const,
    overview: () => ['admin', 'overview'] as const,
    userAccess: (id: string) => ['admin', 'user', id, 'access'] as const,
    userSessions: (id: string) => ['admin', 'user', id, 'sessions'] as const,
    userMcp: (id: string) => ['admin', 'user', id, 'mcp'] as const,
    groups: () => ['admin', 'groups'] as const,
    group: (id: string) => ['admin', 'group', id] as const,
    permissions: () => ['admin', 'permissions'] as const,
    firmBranchOptions: (domain: string) => ['admin', 'firm-branch-options', domain] as const,
    auditLogs: (params?: KeyPart) => ['admin', 'audit-logs', params ?? null] as const,
  },

  /**
   * `/system/sources`: kayıtlı kaynaklar + bağlantı sağlığı + senkron durumu.
   * Kaynak tablosuna değil, backend'in sağlık özetine dayandığı için `source` öneki almaz;
   * SSE köprüsü bu anahtarı ayrıca tazeler (bkz. hooks/use-realtime.ts).
   */
  /** Kullanıcının tablo düzeni; kaynak verisine dayanmaz, SSE ile tazelenmez. */
  tablePreferences: (tableId: string) => ['preferences', 'table', tableId] as const,

  systemSources: () => ['system', 'sources'] as const,

  source: (source: string, table: string, ...rest: KeyPart[]) =>
    ['source', source, table, ...rest] as const,

  /** Kayıtlı rapor tanımları (belge şablonları dahil): `/api/v1/bi/reports*`. SSE ile tazelenmez. */
  bi: {
    reports: () => ['bi', 'reports'] as const,
    report: (id: string) => ['bi', 'report', id] as const,
    versions: (id: string) => ['bi', 'report', id, 'versions'] as const,
    runs: (id: string) => ['bi', 'report', id, 'runs'] as const,
    run: (id: string, runId: string) => ['bi', 'report', id, 'runs', runId] as const,
  },

  /** İK paneli backend uçları (`/api/v1/hr/*`); listeler Cube'dan (`cube(...)`). */
  notifications: () => ['notifications'] as const,
  hr: {
    scope: () => ['hr', 'scope'] as const,
    requestCounts: () => ['hr', 'requests', 'counts'] as const,
    actions: (kind: string, ids: string[]) => ['hr', 'actions', kind, ids] as const,
    history: (kind: string, id: string) => ['hr', 'history', kind, id] as const,
    interventions: () => ['hr', 'interventions'] as const,
    tasks: () => ['hr', 'tasks'] as const,
    processes: () => ['hr', 'processes'] as const,
    queue: () => ['hr', 'queue'] as const,
    processRuns: (key: string) => ['hr', 'processes', key, 'runs'] as const,
    requests: (status?: string) => ['hr', 'requests', status ?? null] as const,
    request: (id: string) => ['hr', 'request', id] as const,
    leaveTypes: () => ['hr', 'leave-types'] as const,
  },

  /** Nakit planı (`/api/v1/cash/*`, docs/NAKIT-PLANI-PLAN.md). */
  cash: {
    firms: () => ['cash', 'firms'] as const,
    weekly: (firm: string, scenario = 'baz') => ['cash', 'weekly', firm, scenario] as const,
    planning: (firm: string) => ['cash', 'planning', firm] as const,
    strip: (firm: string) => ['cash', 'strip', firm] as const,
    cheques: (firm: string) => ['cash', 'cheques', firm] as const,
    caris: (firm: string, q: string) => ['cash', 'caris', firm, q] as const,
    health: (firm: string) => ['cash', 'health', firm] as const,
    realized: (firm: string) => ['cash', 'realized', firm] as const,
    paymentList: (firm: string, weekEnd: string) =>
      ['cash', 'payment-list', firm, weekEnd] as const,
    todo: (firm: string) => ['cash', 'todo', firm] as const,
    variance: (firm: string, weekEnd: string) => ['cash', 'variance', firm, weekEnd] as const,
    manual: (firm: string) => ['cash', 'manual', firm] as const,
    settings: (firm: string) => ['cash', 'settings', firm] as const,
    history: (firm: string, kind: string, key: string) =>
      ['cash', 'history', firm, kind, key] as const,
  },

  /** Bir Cube view'ına atılan sorgu — SSE `data-changed` olayında bu önekle invalidate edilir. */
  cube: (cubeName: string, query?: KeyPart) => ['cube', cubeName, query ?? null] as const,

  /** @deprecated Faz 3 ile birlikte hiçbir yerden çağrılmıyor; Faz 5'te silinir (bkz. plan). */
  reports: {
    tartim: (params: Record<string, unknown>) =>
      ['source', SOURCE_KANTAR, TABLE_TARTIM, 'reports/tartim', params] as const,
    tonaj: (params: Record<string, unknown>) =>
      ['source', SOURCE_KANTAR, TABLE_TARTIM, 'reports/tonaj', params] as const,
    /** Gelişmiş filtre ara-yaz önerileri — en fazla 20 sonuç, gerçek zamanlı yenilemeye ihtiyaç yok. */
    tartimDistinct: (field: string, q: string | undefined) =>
      ['reports/tartim/distinct', field, q ?? null] as const,
  },
} as const
